import { Dropdown } from 'primereact/dropdown'
import { FC, useEffect, useMemo, useState } from 'react'
import PostProcGallery from '../PostProcGallery'
import { SectionEmpty } from './Section'
import { RawRun, getRunCases } from './methodologyDiff'

export type ImageComparisonGridProps = {
  baselineRun: RawRun | null
  proposedRun: RawRun | null
}

/**
 * Baseline vs. proposed post-proc image comparison. Reuses PostProcGallery (the same
 * source as the Headline Data image gallery), pairing the two runs' cases by case name.
 */
const ImageComparisonGrid: FC<ImageComparisonGridProps> = ({ baselineRun, proposedRun }) => {
  const proposedCases = useMemo(() => getRunCases(proposedRun), [proposedRun])
  const baselineByName = useMemo(
    () => new Map(getRunCases(baselineRun).map(c => [c.caseName, c.systemRunId])),
    [baselineRun]
  )
  const [caseName, setCaseName] = useState<string | null>(null)

  useEffect(() => {
    setCaseName(proposedCases[0]?.caseName ?? null)
  }, [proposedCases])

  if (!proposedRun) {
    return <SectionEmpty message="Select a proposed run to compare case images." />
  }
  if (!proposedCases.length) {
    return <SectionEmpty message="The proposed run has no cases with post-proc images." />
  }

  const proposedSys = proposedCases.find(c => c.caseName === caseName)?.systemRunId
  const baselineSys = caseName ? baselineByName.get(caseName) : undefined
  const panelHeader = 'text-xs font-bold px-2 py-1 border-b border-gray-200 dark:border-neutral-70'

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <span className="text-xs font-bold text-gray-600 dark:text-gray-300">Case</span>
        <Dropdown
          value={caseName}
          options={proposedCases.map(c => ({ label: c.caseName, value: c.caseName }))}
          onChange={e => setCaseName(e.value)}
          className="w-56"
          filter
        />
      </div>

      <div className="flex flex-col lg:flex-row gap-3 min-h-[480px]">
        {baselineSys && (
          <div className="flex-1 min-w-0 flex flex-col border border-gray-200 dark:border-neutral-70 rounded">
            <div className={panelHeader}>Baseline · {baselineRun?.name}</div>
            <PostProcGallery
              systemRunId={baselineSys}
              runId={baselineRun?.name ?? ''}
              caseName={caseName ?? ''}
            />
          </div>
        )}
        {proposedSys && (
          <div className="flex-1 min-w-0 flex flex-col border border-gray-200 dark:border-neutral-70 rounded">
            <div className={panelHeader}>Proposed · {proposedRun?.name}</div>
            <PostProcGallery
              systemRunId={proposedSys}
              runId={proposedRun?.name ?? ''}
              caseName={caseName ?? ''}
            />
          </div>
        )}
      </div>

      {!baselineSys && (
        <p className="text-xs text-gray-400 italic">
          No matching case on the baseline run; showing the proposed run only.
        </p>
      )}
    </div>
  )
}

export default ImageComparisonGrid
